type ClerkGetToken = (options?: {
  organizationId?: string
  skipCache?: boolean
}) => Promise<string | null>

interface GetTransportTokenOptions {
  getToken: ClerkGetToken
  organizationId?: string | null
  organizationSlug?: string | null
  requireOrganizationClaims?: boolean
  retryAttempts?: number
  retryDelayMs?: number
}

const DEFAULT_RETRY_ATTEMPTS = 5
const DEFAULT_RETRY_DELAY_MS = 100
const SKIP_CACHE_KEY_PREFIX = "refresh"
const CACHE_KEY_PREFIX = "cached"

const inFlightTokenRequests = new WeakMap<
  ClerkGetToken,
  Map<string, Promise<string | null>>
>()

function wait(ms: number) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms)
  })
}

function decodeBase64Url(value: string) {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/")
  const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=")
  return globalThis.atob(padded)
}

function getJwtClaims(token: string | null) {
  if (!token) {
    return null
  }

  const [, payload] = token.split(".")
  if (!payload) {
    return null
  }

  try {
    return JSON.parse(decodeBase64Url(payload)) as Record<string, unknown>
  } catch (_error: unknown) {
    return null
  }
}

function getStringClaim(claims: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = claims[key]
    if (typeof value === "string") {
      return value
    }
  }

  return undefined
}

function getTokenRequestKey({
  organizationId,
  skipCache,
}: {
  organizationId?: string
  skipCache?: boolean
}) {
  return `${skipCache ? SKIP_CACHE_KEY_PREFIX : CACHE_KEY_PREFIX}:${
    organizationId ?? ""
  }`
}

function getTokenRequestOptions({
  organizationId,
  skipCache,
}: {
  organizationId?: string
  skipCache?: boolean
}) {
  return organizationId || skipCache
    ? {
        organizationId,
        ...(skipCache ? { skipCache } : {}),
      }
    : undefined
}

function getDedupeTransportToken({
  getToken,
  organizationId,
  skipCache,
}: {
  getToken: ClerkGetToken
  organizationId?: string
  skipCache?: boolean
}) {
  const requestKey = getTokenRequestKey({ organizationId, skipCache })
  let tokenRequests = inFlightTokenRequests.get(getToken)

  if (!tokenRequests) {
    tokenRequests = new Map()
    inFlightTokenRequests.set(getToken, tokenRequests)
  }

  const inFlightRequest = tokenRequests.get(requestKey)
  if (inFlightRequest) {
    return inFlightRequest
  }

  const tokenRequest = getToken(
    getTokenRequestOptions({ organizationId, skipCache }),
  ).finally(() => {
    tokenRequests.delete(requestKey)
  })
  tokenRequests.set(requestKey, tokenRequest)

  return tokenRequest
}

function getTokenOrganizationMismatch({
  organizationId,
  organizationSlug,
  requireOrganizationClaims,
  token,
}: {
  organizationId?: string | null
  organizationSlug?: string | null
  requireOrganizationClaims: boolean
  token: string | null
}) {
  const claims = getJwtClaims(token)
  if (!claims) {
    return requireOrganizationClaims && (organizationId || organizationSlug)
      ? "requested organization scope, token claims unavailable"
      : undefined
  }

  const tokenOrganizationId = getStringClaim(claims, [
    "org_id",
    "orgId",
    "organization_id",
    "organizationId",
  ])
  const tokenOrganizationSlug = getStringClaim(claims, [
    "org_slug",
    "orgSlug",
    "organization_slug",
    "organizationSlug",
  ])

  if (organizationId) {
    if (requireOrganizationClaims && !tokenOrganizationId) {
      return `requested org id ${organizationId}, token org id unavailable`
    }
    if (tokenOrganizationId && tokenOrganizationId !== organizationId) {
      return `requested org id ${organizationId}, token org id ${tokenOrganizationId}`
    }
  }

  if (organizationSlug) {
    if (requireOrganizationClaims && !tokenOrganizationSlug) {
      return `requested org slug ${organizationSlug}, token org slug unavailable`
    }
    if (tokenOrganizationSlug && tokenOrganizationSlug !== organizationSlug) {
      return `requested org slug ${organizationSlug}, token org slug ${tokenOrganizationSlug}`
    }
  }

  return undefined
}

export async function getTransportTokenForOrganization({
  getToken,
  organizationId,
  organizationSlug,
  requireOrganizationClaims = false,
  retryAttempts = DEFAULT_RETRY_ATTEMPTS,
  retryDelayMs = DEFAULT_RETRY_DELAY_MS,
}: GetTransportTokenOptions) {
  let lastMismatch: string | undefined
  const attempts = Math.max(1, retryAttempts)

  for (let attempt = 1; attempt <= attempts; attempt++) {
    const token = await getDedupeTransportToken({
      getToken,
      organizationId: organizationId ?? undefined,
      skipCache: attempt > 1,
    })
    lastMismatch = getTokenOrganizationMismatch({
      organizationId,
      organizationSlug,
      requireOrganizationClaims,
      token,
    })

    if (!lastMismatch) {
      return token
    }

    if (attempt < attempts && retryDelayMs > 0) {
      await wait(retryDelayMs)
    }
  }

  throw new Error("Transport auth token organization mismatch.")
}
